import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, CheckCircle2, Loader2, Play, MessageSquare, FileText, Copy, Terminal as TerminalIcon } from "lucide-react";
import { toast } from "sonner";
import { useT, useLang } from "@/lib/i18n";
import RatchetLogo from "@/components/RatchetLogo";
import InvestigationTerminal from "@/components/InvestigationTerminal";
import InteractiveTerminal from "@/components/InteractiveTerminal";
import RatchetREPL from "@/components/RatchetREPL";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function CardDetail() {
  const t = useT();
  const { lang } = useLang();
  const { id } = useParams();
  const [card, setCard] = useState(null);
  const [investigating, setInvestigating] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [showTerm, setShowTerm] = useState(false);
  const [activePanel, setActivePanel] = useState("ratchet"); // 'ratchet' | 'logs' | 'code'
  const [spec, setSpec] = useState(null);
  const [termConfig, setTermConfig] = useState(null); // {available, mode, bridge_connected?}
  const [interactiveStarted, setInteractiveStarted] = useState(false);
  const pollRef = useRef(null);
  const token = typeof window !== "undefined" ? localStorage.getItem("ratchet_token") : null;

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get(`/cards/${id}`);
        setCard(data);
        if (data.status === "complete") clearInterval(pollRef.current);
      } catch {/* ignore — poll will retry */}
    };
    load();
    pollRef.current = setInterval(load, 1000);
    return () => clearInterval(pollRef.current);
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/cards/${id}/job`);
        if (!cancelled) setJobId(data?.job?.id || null);
      } catch {/* job may not exist */}
      try {
        const { data } = await api.get(`/cards/${id}/terminal-config`);
        if (!cancelled) setTermConfig(data || null);
      } catch {/* terminal not available */}
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Poll spec while ratchet pane is open
  useEffect(() => {
    let cancelled = false;
    const loadSpec = async () => {
      try {
        const { data } = await api.get(`/cards/${id}/spec`);
        if (!cancelled) setSpec(data?.spec || null);
      } catch {/* ignore */}
    };
    loadSpec();
    const t = setInterval(loadSpec, 2000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id]);

  const investigate = async () => {
    setInvestigating(true);
    try {
      await api.post(`/cards/${id}/investigate`);
      toast.success(lang === "pt" ? "Investigação iniciada" : "Investigation started");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setInvestigating(false);
    }
  };

  if (!card) return <div className="p-12 text-white/40">{t("common.loading")}</div>;

  const isAwaiting = card.status === "awaiting_user";

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <Link to="/app/cards" className="inline-flex items-center gap-1 text-sm text-white/50 hover:text-white mb-6" data-testid="card-back-link">
        <ArrowLeft className="w-4 h-4" /> {t("card.back")}
      </Link>
      <div className="overline mb-2">card · {card.external_id}</div>
      <h1 className="font-display font-black text-3xl tracking-tighter mb-4">{card.title}</h1>
      <div className="flex items-center gap-3 text-xs font-mono text-white/50 mb-8">
        <span className={`status-dot ${card.status === "complete" ? "bg-emerald-400" : isAwaiting ? "bg-white/40" : "bg-amber-400 animate-pulse"}`} />
        <span>{card.status}</span>
        <span>·</span>
        <span>scenario: {card.scenario}</span>
        <span>·</span>
        <span>{card.progress ?? 0}%</span>
      </div>

      {isAwaiting && (
        <div className="surface p-5 mb-6 flex items-center justify-between gap-4 border-primary/40" data-testid="card-awaiting-banner">
          <div className="text-sm">
            <div className="font-display font-bold text-base mb-1">
              {lang === "pt" ? "Investigação manual ativada" : "Manual investigation mode"}
            </div>
            <div className="text-white/60 text-xs">
              {lang === "pt"
                ? "A IA não inicia automaticamente. Clique abaixo para começar a análise."
                : "AI does not start automatically. Click below to begin the analysis."}
            </div>
          </div>
          <button onClick={investigate} disabled={investigating} className="btn-primary text-sm whitespace-nowrap" data-testid="card-investigate-btn">
            {investigating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {lang === "pt" ? "Investigar agora" : "Investigate now"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 surface p-6">
          <div className="overline mb-4">{t("card.pipeline")}</div>
          <div className="space-y-2">
            {card.steps_completed?.map((s, i) => (
              <div key={i} className="flex items-center gap-3 text-sm" data-testid={`card-step-${s.key}`}>
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="text-white/80">{s.label}</span>
              </div>
            ))}
            {card.status !== "complete" && !isAwaiting && (
              <div className="flex items-center gap-3 text-sm text-white/60" data-testid="card-processing-indicator">
                <RatchetLogo size={20} spinning />
                <span>{t("demo.working")}...</span>
              </div>
            )}
          </div>
          {jobId && (
            <div className="mt-6 pt-4 border-t border-white/10">
              <button
                onClick={() => setShowTerm((v) => !v)}
                className="text-xs font-mono text-white/60 hover:text-white inline-flex items-center gap-2"
                data-testid="card-toggle-terminal-btn"
              >
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                {showTerm
                  ? (lang === "pt" ? "Ocultar terminal" : lang === "es" ? "Ocultar terminal" : "Hide terminal")
                  : (lang === "pt" ? "Abrir terminal (Ratchet · Bridge Logs)" : lang === "es" ? "Abrir terminal (Ratchet · Bridge Logs)" : "Open terminal (Ratchet · Bridge Logs)")}
              </button>
              <div className="text-[10px] text-white/30 mt-1 font-mono">
                {lang === "pt"
                  ? "Conversa socrática (Ratchet) + stream do bridge daemon"
                  : lang === "es"
                  ? "Conversación socrática (Ratchet) + stream del bridge daemon"
                  : "Socratic chat (Ratchet) + bridge daemon stream"}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-7 surface p-6">
          <div className="overline mb-4">{t("card.aiReport")}</div>
          {!card.report && <div className="text-white/40 text-sm">{t("card.awaiting")}</div>}
          {card.report && (
            <div className="space-y-6">
              <Section title={t("demo.report.hypotheses")} color="text-amber-400" items={card.report.hypotheses} />
              <Section title={t("demo.report.facts")} color="text-blue-400" items={card.report.facts} mono />
              <div className="border-l-2 border-emerald-400 pl-4">
                <div className="overline text-emerald-400 mb-2">{t("demo.report.rootCause")}</div>
                <p className="text-sm text-white/90">{card.report.root_cause}</p>
              </div>
              <div className="border-l-2 border-primary pl-4">
                <div className="overline text-primary mb-2">{t("demo.report.resolution")}</div>
                <p className="text-sm text-white/90">{card.report.resolution}</p>
              </div>
              {card.report.classification && (
                <div className="text-xs text-white/50 font-mono border-l-2 border-white/20 pl-3" data-testid="card-classification">
                  <span className="text-white/40">classified as</span>{" "}
                  <span className="text-primary">{card.report.classification.scenario}</span>
                  <span className="text-white/40"> · confidence {Number(card.report.classification.confidence || 0).toFixed(2)}</span>
                  {card.report.classification.used_fallback && <span className="text-yellow-400/70"> · fallback (low confidence)</span>}
                  {card.report.classification.reason && <div className="text-white/40 mt-0.5">{card.report.classification.reason}</div>}
                </div>
              )}
              <div className="flex flex-wrap justify-between gap-2 border-t border-white/10 pt-4 font-mono text-xs text-white/40">
                <span>model · {card.report.ai_model}</span>
                {Number(card.report.kb_patterns_used) > 0 && (
                  <Link
                    to="/app/knowledge"
                    className="text-emerald-400/80 hover:text-emerald-400 hover:underline"
                    title={lang === "pt" ? "Padrões históricos usados como contexto nesta investigação" : "Historical patterns used as context in this investigation"}
                    data-testid="card-kb-used"
                  >
                    KB · {card.report.kb_patterns_used} {lang === "pt" ? "padrões usados" : "patterns used"}
                  </Link>
                )}
                {card.report.rag_used && (
                  <span
                    className="text-primary/80"
                    title={[
                      `repo · ${card.report.rag_used.repo_integration_id || "—"}`,
                      `profiles · ${card.report.rag_used.profiles_count || 0}`,
                      card.report.rag_used.schema_included ? "schema ✓" : "schema ✗",
                      card.report.rag_used.logs_included ? "logs ✓" : "logs ✗",
                      card.report.rag_used.flow_generated ? `flow auto (${card.report.rag_used.flow_generated_steps_count || 0} steps)` : null,
                    ].filter(Boolean).join(" · ")}
                    data-testid="card-rag-used"
                  >
                    RAG · {card.report.rag_used.profiles_count || 0} {lang === "pt" ? "repos" : "repos"}
                    {card.report.rag_used.flow_generated ? " · auto-flow" : ""}
                  </span>
                )}
                <span>⏱ {t("demo.report.saved")} ~{card.report.time_saved_minutes} min</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {showTerm && jobId && token && (
        <div className="mt-6 space-y-3" data-testid="card-terminal-container">
          <div className="inline-flex border border-white/10 rounded-sm overflow-hidden text-[10px] font-mono uppercase tracking-wider">
            <button
              onClick={() => setActivePanel("ratchet")}
              className={`px-3 py-1.5 flex items-center gap-1.5 ${activePanel === "ratchet" ? "bg-white/10 text-white" : "text-white/50 hover:text-white"}`}
              data-testid="panel-tab-ratchet"
            >
              <MessageSquare className="w-3 h-3" />
              ratchet
            </button>
            <button
              onClick={() => setActivePanel("logs")}
              className={`px-3 py-1.5 flex items-center gap-1.5 ${activePanel === "logs" ? "bg-white/10 text-white" : "text-white/50 hover:text-white"}`}
              data-testid="panel-tab-logs"
            >
              <FileText className="w-3 h-3" />
              bridge logs
            </button>
            {termConfig?.available && (
              <button
                onClick={() => { setActivePanel("code"); setInteractiveStarted(true); }}
                className={`px-3 py-1.5 flex items-center gap-1.5 ${activePanel === "code" ? "bg-white/10 text-white" : "text-white/50 hover:text-white"}`}
                data-testid="panel-tab-code"
                title={termConfig.mode === "bridge" && !termConfig.bridge_connected
                  ? (lang === "pt" ? "Bridge daemon não conectado" : "Bridge daemon not connected")
                  : ""}
              >
                <TerminalIcon className="w-3 h-3" />
                code · {termConfig.mode}
                {termConfig.mode === "bridge" && (
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${termConfig.bridge_connected ? "bg-emerald-400" : "bg-amber-400"}`} />
                )}
              </button>
            )}
          </div>
          <div style={{ display: activePanel === "ratchet" ? "block" : "none" }}>
            <RatchetREPL
              cardId={id}
              apiBase={API_BASE}
              token={token}
              onFinalize={(d) => setSpec((s) => ({ ...(s || {}), ...d, status: "finalized" }))}
              onSkip={() => setSpec((s) => ({ ...(s || {}), status: "skipped" }))}
            />
          </div>
          <div style={{ display: activePanel === "logs" ? "block" : "none" }}>
            <InvestigationTerminal jobId={jobId} apiBase={API_BASE} token={token} />
          </div>
          {termConfig?.available && interactiveStarted && (
            <div style={{ display: activePanel === "code" ? "block" : "none" }}>
              <InteractiveTerminal
                cardId={id}
                mode={termConfig.mode}
                apiBase={API_BASE}
                token={token}
                context={card?.report ? JSON.stringify(card.report, null, 2) : ""}
              />
            </div>
          )}
        </div>
      )}

      {spec && (spec.spec_yaml || spec.suggested_response) && (
        <SpecBlock spec={spec} lang={lang} />
      )}
    </div>
  );
}

function SpecBlock({ spec, lang }) {
  const copy = (text) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(lang === "pt" ? "Copiado" : lang === "es" ? "Copiado" : "Copied");
    } catch {
      toast.error("Failed to copy");
    }
  };
  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="card-spec-block">
      {spec.spec_yaml && (
        <div className="surface p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="overline text-cyan-400">{lang === "pt" ? "Spec do problema" : lang === "es" ? "Spec del problema" : "Problem spec"}</div>
            <button
              onClick={() => copy(spec.spec_yaml)}
              className="text-[10px] uppercase tracking-wider text-white/50 hover:text-white inline-flex items-center gap-1"
              data-testid="spec-copy-btn"
            ><Copy className="w-3 h-3" />copy</button>
          </div>
          <pre className="text-xs font-mono text-white/80 whitespace-pre-wrap leading-relaxed max-h-96 overflow-auto">{spec.spec_yaml}</pre>
          <div className="mt-3 pt-3 border-t border-white/5 font-mono text-[10px] text-white/30">
            {spec.provider_used && <>provider · {spec.provider_used}{spec.model_used ? ` · ${spec.model_used}` : ""}</>}
          </div>
        </div>
      )}
      {spec.suggested_response && (
        <div className="surface p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="overline text-primary">{lang === "pt" ? "Resposta sugerida (para o board)" : lang === "es" ? "Respuesta sugerida (para el tablero)" : "Suggested response (post to board)"}</div>
            <button
              onClick={() => copy(spec.suggested_response)}
              className="text-[10px] uppercase tracking-wider text-white/50 hover:text-white inline-flex items-center gap-1"
              data-testid="response-copy-btn"
            ><Copy className="w-3 h-3" />copy</button>
          </div>
          <div className="text-sm text-white/85 whitespace-pre-wrap leading-relaxed max-h-96 overflow-auto">{spec.suggested_response}</div>
        </div>
      )}
    </div>
  );
}

function Section({ title, color, items, mono }) {
  return (
    <div>
      <div className={`overline mb-2 ${color}`}>{title}</div>
      <ul className={`space-y-1.5 text-sm text-white/80 list-disc list-inside ${mono ? "font-mono text-xs" : ""}`}>
        {items.map((x, i) => <li key={i}>{x}</li>)}
      </ul>
    </div>
  );
}
